import { useTerminalsStateRE } from "@/States/Terminals";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { getTenantUrl } from "@/Utils/getTenantUrl";
import { trpc } from "@/Utils/trpc";
import { timezoneStore } from "@/api/hooks/useConfig";
import { defaultTimeFilter } from "@/components/v2/DatePicker/constants";
import { ViewWithLoader } from "@/components/v2/ViewWithLoader";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useTrans } from "@/translations";

import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { Granularity } from "server/dwhApi/common.js";
import { twMerge } from "tailwind-merge";

import { ReportingPageContext } from "../../context";
import {
  REPORTING_TABLE_UNIT,
  REPORTING_TAB_TYPES,
  REPORTING_VIEW_TYPES,
  ReportingSortingDirection,
} from "../../types";
import { getReportingActiveData } from "./components/dataFunctions/activeData";
import { getReportingConversionFunction } from "./components/dataFunctions/conversionFunction";
import {
  NAME_COLUMN_ID,
  getReportingTableHeaders,
} from "./components/dataFunctions/headers";
import {
  buildCustomerRows,
  buildPeriodRows,
  buildTerminalRows,
  buildTotalsRow,
  sortRows,
} from "./components/dataFunctions/rowsFunction";
import { getWasteColumns } from "./components/dataFunctions/wasteColumns";
import { ReportingFooter } from "./components/footer";
import { ReportingExportButton } from "./components/headerWithSelectors/export/button";
import { ReportingGranularityFilter } from "./components/headerWithSelectors/granularityFilter";
import { ReportingTableTitle } from "./components/headerWithSelectors/tableTitle";
import { ReportingUnitFilter } from "./components/headerWithSelectors/unitFilter";
import { ReportingZonesToggle } from "./components/headerWithSelectors/zoneToggle";
import { ReportingTable } from "./components/table";
import { ReportingTableContext } from "./context";
import type { TableRow } from "./types";

export const ReportingTableView = () => {
  const {
    tabType,
    viewType,
    timeFilter,
    selectedBuildingIds,
    selectedTenantCategoryIds,
  } = useContext(ReportingPageContext);
  const { timezone } = timezoneStore();
  const { language } = useTrans();
  const { currentTerminal } = useTerminalsStateRE();
  const { wasteTypes: sanityWasteTypes, downstreamHandling } =
    useCommonEntitiesStore();
  const { wasteTypeConfig, wasteTypeClientConfig, terminalWasteTypeConfig } =
    useConfigService();

  const [granularity, setGranularity] = useState<Granularity>(Granularity.WEEK);
  const [unit, setUnit] = useState<REPORTING_TABLE_UNIT>(
    REPORTING_TABLE_UNIT.WEIGHT,
  );
  const [showZones, setShowZones] = useState<boolean>(false);
  const [sortKey, setSortKey] = useState<string>(NAME_COLUMN_ID);
  const [sortDirection, setSortDirection] = useState<ReportingSortingDirection>(
    ReportingSortingDirection.ASC,
  );
  const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setSelectedRows({});
  }, [viewType, tabType]);

  useEffect(() => {
    if (
      viewType === REPORTING_VIEW_TYPES.PORTFOLIO &&
      unit === REPORTING_TABLE_UNIT.COST
    ) {
      setUnit(REPORTING_TABLE_UNIT.WEIGHT);
    }
  }, [viewType, unit]);

  const onSort = useCallback(
    (key: string) => {
      setSortDirection(
        sortKey === key && sortDirection === ReportingSortingDirection.ASC
          ? ReportingSortingDirection.DESC
          : ReportingSortingDirection.ASC,
      );
      setSortKey(key);
    },
    [sortKey, sortDirection],
  );

  const isPortfolioView = useMemo(
    () => viewType === REPORTING_VIEW_TYPES.PORTFOLIO,
    [viewType],
  );
  const isBuildingView = useMemo(
    () => viewType === REPORTING_VIEW_TYPES.BUILDING,
    [viewType],
  );

  const isTotalTab = useMemo(
    () => tabType === REPORTING_TAB_TYPES.TOTAL,
    [tabType],
  );
  const isIndividualTab = useMemo(
    () => tabType === REPORTING_TAB_TYPES.INDIVIDUAL,
    [tabType],
  );

  const { data: aggregatesData, isLoading: isAggregatesLoading } =
    trpc.dwh.terminals.getAggregatesPeriods.useQuery(
      {
        ...(timeFilter || defaultTimeFilter),
        timeZone: timezone,
        granularity: granularity,
        terminalIds: selectedBuildingIds || [],
      },
      {
        queryKeyHashFn: () =>
          [
            "terminalAggregatePeriods",
            timeFilter?.startTimeISO,
            timeFilter?.endTimeISO,
            timezone,
            granularity,
            selectedBuildingIds,
          ].toString(),
        enabled:
          !!timeFilter &&
          !!selectedBuildingIds &&
          isPortfolioView &&
          isTotalTab,
      },
    );

  const { data: terminalsData, isLoading: isLoadingTerminals } =
    trpc.dwh.terminals.get.useQuery(timeFilter || defaultTimeFilter, {
      queryKeyHashFn: () =>
        [
          "terminals",
          timeFilter?.startTimeISO,
          timeFilter?.endTimeISO,
        ].toString(),
      enabled: !!timeFilter && isPortfolioView && isIndividualTab,
    });

  const { data: periodData, isLoading: isPeriodLoading } =
    trpc.dwh.terminals.getPeriods.useQuery(
      {
        ...(timeFilter || defaultTimeFilter),
        timeZone: timezone,
        terminalId: currentTerminal?.id || "",
        granularity: granularity,
        tenantCategories: selectedTenantCategoryIds || [],
      },
      {
        queryKeyHashFn: () =>
          [
            "terminalPeriods",
            timeFilter?.startTimeISO,
            timeFilter?.endTimeISO,
            timezone,
            currentTerminal?.id,
            granularity,
            selectedTenantCategoryIds,
          ].toString(),
        enabled:
          !!timeFilter &&
          !!currentTerminal &&
          isBuildingView &&
          isTotalTab &&
          !!selectedTenantCategoryIds?.length,
      },
    );

  const { data: customersData, isLoading: isLoadingCustomers } =
    trpc.dwh.customers.get.useQuery(
      {
        ...(timeFilter || defaultTimeFilter),
        terminalId: currentTerminal?.id || "",
        tenantCategories: selectedTenantCategoryIds || [],
      },
      {
        queryKeyHashFn: () =>
          [
            "customers",
            currentTerminal?.id,
            timeFilter?.startTimeISO,
            timeFilter?.endTimeISO,
            selectedTenantCategoryIds,
          ].toString(),
        enabled:
          !!timeFilter &&
          !!currentTerminal &&
          isBuildingView &&
          isIndividualTab,
      },
    );

  const { data: accessCodes } =
    trpc.scanner.customers.getAllAccessCodes.useQuery(
      { terminalId: currentTerminal?.id || "" },
      {
        queryKeyHashFn: () =>
          ["customersAccessCodes", currentTerminal?.id].toString(),
        enabled: !!currentTerminal && isBuildingView && isIndividualTab,
      },
    );

  const tenantUrls = useMemo(
    () =>
      Object.fromEntries(
        (accessCodes?.data ?? []).map((el) => [
          el.storeUuid,
          getTenantUrl(el.accessCode),
        ]),
      ),
    [accessCodes],
  );

  const isLoading = useMemo(
    () =>
      !timeFilter ||
      isAggregatesLoading ||
      isLoadingTerminals ||
      isPeriodLoading ||
      isLoadingCustomers ||
      !selectedTenantCategoryIds,
    [
      timeFilter,
      isAggregatesLoading,
      isLoadingCustomers,
      isLoadingTerminals,
      isPeriodLoading,
      selectedTenantCategoryIds,
    ],
  );

  const activeData = useMemo(
    () =>
      getReportingActiveData({
        viewType,
        tabType,
        aggregatesData,
        terminalsData,
        periodData,
        customersData,
        selectedBuildingIds,
      }),
    [
      viewType,
      tabType,
      aggregatesData,
      terminalsData,
      periodData,
      customersData,
      selectedBuildingIds,
    ],
  );

  const hasZones = useMemo(
    () =>
      !!activeData.customers?.some(
        (customer) => !!customer.customerId && !!customer.children?.length,
      ),
    [activeData],
  );

  useEffect(() => {
    if (!isLoading && !hasZones) {
      setShowZones(false);
    }
  }, [isLoading, hasZones]);

  const wasteColumns = useMemo(
    () =>
      getWasteColumns(
        activeData,
        sanityWasteTypes,
        wasteTypeConfig,
        downstreamHandling,
      ),
    [activeData, sanityWasteTypes, wasteTypeConfig, downstreamHandling],
  );

  const convert = useMemo(
    () =>
      getReportingConversionFunction({
        viewType,
        wasteTypeClientConfig,
        terminalWasteTypeConfig,
        unit,
      }),
    [viewType, wasteTypeClientConfig, terminalWasteTypeConfig, unit],
  );

  const headers = useMemo(
    () =>
      getReportingTableHeaders({
        viewType,
        tabType,
        wasteColumns,
        showZones,
        sortKey,
        sortDirection,
        onSort,
      }),
    [
      viewType,
      tabType,
      wasteColumns,
      showZones,
      sortKey,
      sortDirection,
      onSort,
    ],
  );

  const rows = useMemo(() => {
    let rows: TableRow[] = [];
    if (activeData.periods) {
      rows = buildPeriodRows(
        activeData.periods,
        granularity,
        wasteColumns,
        convert,
        language,
        timezone,
        timeFilter,
      );
    } else if (activeData.terminals) {
      rows = buildTerminalRows(activeData.terminals, wasteColumns, convert);
    } else if (activeData.customers) {
      // Unit rows link with the unit's own access code.
      rows = buildCustomerRows(
        activeData.customers,
        showZones,
        wasteColumns,
        convert,
      ).map((row) => ({
        ...row,
        link: (row.id && tenantUrls[row.id]) || undefined,
      }));
    }
    return isIndividualTab ? sortRows(rows, headers) : rows;
  }, [
    activeData,
    granularity,
    wasteColumns,
    convert,
    showZones,
    isIndividualTab,
    headers,
    tenantUrls,
    language,
    timezone,
    timeFilter,
  ]);

  const totalsRow = useMemo(
    () => buildTotalsRow(rows, wasteColumns),
    [rows, wasteColumns],
  );

  const nrTotal = useMemo(() => rows.length, [rows]);
  const nrSelected = useMemo(
    () => rows.filter((row) => selectedRows[row.key]).length,
    [rows, selectedRows],
  );

  const onToggleRow = useCallback(
    (key: string) =>
      setSelectedRows((prev) => ({ ...prev, [key]: !prev[key] })),
    [],
  );

  const onToggleAll = useCallback(() => {
    const selectAll = nrSelected < nrTotal;
    setSelectedRows(
      selectAll ? Object.fromEntries(rows.map((row) => [row.key, true])) : {},
    );
  }, [rows, nrSelected, nrTotal]);

  const headersWithSelection = useMemo(
    () =>
      headers.map((header) =>
        header.isCheckbox
          ? { ...header, nrTotal, nrSelected, onClick: onToggleAll }
          : header,
      ),
    [headers, nrTotal, nrSelected, onToggleAll],
  );

  const context = useMemo(
    () => ({
      granularity,
      setGranularity,
      unit,
      setUnit,
      showZones,
      setShowZones,
      nrSelected,
      nrTotal,
      isLoading,
    }),
    [
      granularity,
      unit,
      showZones,
      setShowZones,
      nrSelected,
      nrTotal,
      isLoading,
    ],
  );

  return (
    <ReportingTableContext.Provider value={context}>
      <div className="border-grey2 mt-[26px] overflow-hidden rounded-xl border bg-white">
        <div className="border-b-grey2 flex flex-wrap items-center justify-between gap-[16px] border-b px-[22px] py-[16px]">
          <ReportingTableTitle />
          <div
            className={twMerge(
              "flex flex-wrap items-center",
              isTotalTab ? "gap-[10px]" : "gap-[14px]",
            )}
          >
            {isTotalTab && <ReportingGranularityFilter />}
            <ReportingUnitFilter />
            {isBuildingView && isIndividualTab && hasZones && (
              <ReportingZonesToggle />
            )}
            <ReportingExportButton
              rows={rows}
              selectedRows={selectedRows}
              wasteColumns={wasteColumns}
            />
          </div>
        </div>
        <ViewWithLoader isLoading={isLoading} loaderClassName="h-[300px]">
          <ReportingTable
            headers={headersWithSelection}
            rows={rows}
            totalsRow={totalsRow}
            selectedRows={selectedRows}
            onToggleRow={onToggleRow}
          />
          {isIndividualTab && <ReportingFooter />}
        </ViewWithLoader>
      </div>
    </ReportingTableContext.Provider>
  );
};
